import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { SlotBookDialog } from "@/components/enhancer/SlotBookDialog";

const H0 = 7, H1 = 21;
const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + (m || 0); };
const fmt12 = (m) => { const h = Math.floor(m / 60); return `${h % 12 || 12}${m % 60 ? `:${String(m % 60).padStart(2, "0")}` : ""}${h >= 12 ? "pm" : "am"}`; };
const iso = (d) => d.toLocaleDateString("sv-SE", { timeZone: "America/New_York" });
const addDays = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const STYLE = {
  open: "bg-[#5C8A72]/10 hover:bg-[#5C8A72]/25 text-[#3F6B55] cursor-pointer",
  taken: "bg-[#E5E0D8] text-[#8FA89B] cursor-not-allowed",
  mine: "bg-[#144F75] text-white cursor-pointer",
  pending: "bg-[#D98E73]/30 text-[#8A4A33] cursor-pointer",
  closed: "bg-[repeating-linear-gradient(45deg,#F5F1EA,#F5F1EA_4px,#EDE6DA_4px,#EDE6DA_8px)] text-[#B8B0A3]",
};
const LEGEND = [["open", "Available"], ["taken", "Booked by others"], ["mine", "Booked by you"], ["pending", "Awaiting manager"], ["closed", "Center closed"]];

const cellState = (day, blocks, s, e) => {
  if (day.closed || !day.open.some(([a, b]) => toMin(a) <= s && e <= toMin(b))) return { st: "closed" };
  const hit = blocks.find((b) => toMin(b.start) < e && s < toMin(b.end));
  return hit ? { st: hit.status, block: hit } : { st: "open" };
};

export const CenterCalendar = ({ centers }) => {
  const qc = useQueryClient();
  const [lid, setLid] = useState(centers[0]?.id || "");
  const [start, setStart] = useState(iso(new Date()));
  const [dayIdx, setDayIdx] = useState(0);
  const [pick, setPick] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["center-calendar", lid, start], enabled: !!lid, refetchInterval: 30000,
    queryFn: () => api.get(`/enhancer/centers/${lid}/calendar`, { params: { start, days: 7 } }).then((r) => r.data),
  });
  const day = data?.days?.[dayIdx];
  const hours = useMemo(() => Array.from({ length: H1 - H0 }, (_, i) => (H0 + i) * 60), []);
  const busyMe = (s, e) => (day?.mine || []).find((m) => toMin(m.start) < e && s < toMin(m.end));
  const refresh = () => qc.invalidateQueries({ queryKey: ["center-calendar"] });

  return (
    <div data-testid="center-calendar">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={lid} onChange={(e) => { setLid(e.target.value); setDayIdx(0); }} className="eyw-input" data-testid="center-calendar-center">
          {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => { setStart(addDays(start, -7)); setDayIdx(0); }} disabled={start <= iso(new Date())} className="eyw-btn-ghost !p-2 disabled:opacity-40" data-testid="center-calendar-prev" aria-label="Previous week"><ChevronLeft size={16} /></button>
          <button onClick={() => { setStart(iso(new Date())); setDayIdx(0); }} className="eyw-btn-ghost !py-1.5 !px-3 text-xs" data-testid="center-calendar-today">Today</button>
          <button onClick={() => { setStart(addDays(start, 7)); setDayIdx(0); }} className="eyw-btn-ghost !p-2" data-testid="center-calendar-next" aria-label="Next week"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="center-calendar-days">
        {Array.from({ length: 7 }, (_, i) => addDays(start, i)).map((d, i) => {
          const dt = new Date(`${d}T12:00:00`);
          return (
            <button key={d} onClick={() => setDayIdx(i)} data-testid={`center-calendar-day-${d}`}
              className={`shrink-0 rounded-2xl px-4 py-2 text-center transition-colors ${i === dayIdx ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#1A242B] hover:border-[#144F75]"}`}>
              <span className="block text-[11px] uppercase tracking-wide opacity-75">{dt.toLocaleDateString([], { weekday: "short" })}</span>
              <span className="block font-serif text-lg leading-tight">{dt.getDate()}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mb-4 text-xs" data-testid="center-calendar-legend">
        {LEGEND.map(([k, l]) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-3.5 h-3.5 rounded ${STYLE[k].split(" ").filter((c) => c.startsWith("bg-")).join(" ")}`} />{l}</span>)}
        <span className="inline-flex items-center gap-1.5"><AlertTriangle size={13} className="text-[#C0563A]" /> You're busy elsewhere</span>
      </div>
      {isLoading && <div className="py-16 grid place-items-center text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>}
      {error && <p className="text-sm text-red-600" data-testid="center-calendar-error">{error.response?.data?.detail || "Couldn't load this calendar."}</p>}
      {day && (
        <div className="overflow-x-auto rounded-2xl border border-[#E5E0D8] bg-white">
          <table className="w-full text-xs border-collapse min-w-[560px]" data-testid="center-calendar-grid">
            <thead>
              <tr className="bg-[#F9F7F3] text-[#4A5D6B]">
                <th className="p-2 text-left w-16 font-medium">Time</th>
                <th className="p-2 text-left font-medium min-w-[150px]"><span className="inline-flex items-center gap-1"><CalendarDays size={12} /> My schedule</span></th>
                {data.rooms.map((r) => (
                  <th key={r.key} className="p-2 text-left font-medium min-w-[120px]">{r.name}{r.requires_approval && <span className="block text-[10px] text-[#B5794E] font-normal">Manager approval</span>}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map((s) => {
                const e = s + 60;
                const mine = busyMe(s, e);
                return (
                  <tr key={s} className="border-t border-[#F0EBE3]">
                    <td className="p-2 text-[#8FA89B] whitespace-nowrap align-top">{fmt12(s)}</td>
                    <td className="p-1 align-top" data-testid={`my-schedule-${s}`}>
                      {mine && <div className="rounded-lg bg-[#F5E6E0] text-[#8A4A33] px-2 py-1"><span className="font-medium">{mine.label}</span><span className="block opacity-80">{mine.start}–{mine.end}{mine.where ? ` · ${mine.where}` : ""}</span></div>}
                    </td>
                    {data.rooms.map((r) => {
                      const { st, block } = cellState(day, day.rooms[r.key] || [], s, e);
                      const conflict = st === "open" && mine;
                      const click = st === "open" ? () => setPick({ room: r, date: day.date, start: s, conflict: mine })
                        : (st === "mine" || st === "pending") && block?.hold_id ? () => setPick({ room: r, date: day.date, hold: block }) : undefined;
                      return (
                        <td key={r.key} className="p-1 align-top">
                          <button type="button" onClick={click} disabled={!click} data-testid={`slot-${r.key}-${s}`} data-state={st}
                            className={`w-full min-h-[40px] rounded-lg px-2 py-1 text-left transition-colors ${STYLE[st]} ${conflict ? "ring-2 ring-[#C0563A]/60" : ""}`}>
                            {st === "open" ? <span className="inline-flex items-center gap-1">{conflict && <AlertTriangle size={11} className="text-[#C0563A]" />}Open</span>
                              : st === "closed" ? "" : <span className="line-clamp-2">{block?.label}{st === "pending" ? " · pending" : ""}</span>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.rooms.length === 0 && <p className="p-6 text-sm text-[#4A5D6B]">This center hasn't listed any rooms yet.</p>}
        </div>
      )}
      {pick && <SlotBookDialog pick={pick} offerings={data?.offerings || []} onClose={() => setPick(null)} onDone={() => { setPick(null); refresh(); }} />}
    </div>
  );
};
